import { BusyWatcher, Router, watchBusy } from "@frui.ts/screens";
import LocalizationService from "services/localizationService";
import { action, computed, observable } from "mobx";
import type { interfaces } from "inversify";
import DetailViewModel from "viewModels/detailViewModel";
import type TransferPaymentDetailResponse from "entities/transferPaymentDetailResponse";
import PaymentsRepository from "repositories/paymentsRepository";
import { Modules } from "config";
import EnumService from "services/enumService";
import CounterpartyBankAccountType from "entities/counterpartyBankAccountType";
import { emptyValue } from "utils/emptyValue";
import { formatBankAccount } from "@edaz/utils/bankAccount";

@Router.registerRoute({ name: Router.Self, route: ":paymentId" })
export default class PaymentBankTransferDetailViewModel extends DetailViewModel<TransferPaymentDetailResponse> {
  protected readonly modelName = "paymentBankTransfer";
  @observable busyWatcher: BusyWatcher = new BusyWatcher();
  @observable protected itemId: string;

  constructor(
    itemId: string,
    private repository: PaymentsRepository,
    public localizationService: LocalizationService,
    protected enumService: EnumService
  ) {
    super(itemId);
  }

  onInitialize() {
    return super.onInitialize();
  }

  @watchBusy
  protected async loadDetail(): Promise<TransferPaymentDetailResponse> {
    return await this.repository.getBankTransferPaymentDetail(this.itemId);
  }

  @computed
  get isBusy() {
    return this.busyWatcher.isBusy;
  }

  @computed
  get isAmountPositive() {
    return this.item?.amount > 0;
  }

  @computed
  get variableSymbol() {
    return this.item?.variableSymbol;
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.finance, `payments.bankTransferDetail.${code}`, size);
  }

  translateBankAccountType(code: string, size?: number): string {
    return this.localizationService.translateModule(
      Modules.finance,
      `payments.bankTransferDetail.bankAccountTypeEnum.${code}`,
      size
    );
  }

  formatBankAccount(value: string | { accountNumber: string }): string {
    if (!value) {
      return emptyValue;
    }
    if (typeof value === "string") {
      return this.item.counterpartyAccountType === CounterpartyBankAccountType.Domestic
        ? formatBankAccount({ accountNumber: value })
        : value;
    } else {
      return value.accountNumber;
    }
  }

  translatePaymentType(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.finance, `payments.bankTransferDetail.paymentTypeEnum.${code}`, size);
  }

  translateOperationType(code: string, size?: number): string {
    return this.localizationService.translateModule(
      Modules.finance,
      `payments.bankTransferDetail.operationTypeEnum.${code}`,
      size
    );
  }

  @action.bound
  translatePaymentTemplate(paymentTemplateId: string) {
    const paymentTemplate = this.enumService
      .values("payment_templates")
      ?.find(paymentTemplate => paymentTemplate.id === paymentTemplateId);

    if (!paymentTemplate) {
      return;
    }

    return paymentTemplate.messageTemplate;
  }

  static Factory({ container }: interfaces.Context) {
    return (itemId: string) =>
      new PaymentBankTransferDetailViewModel(
        itemId,
        container.get(PaymentsRepository),
        container.get(LocalizationService),
        container.get(EnumService)
      );
  }
}
